/* AdminMgmtHub — combined oversight surface at /admin/mgmt-hub.
 *
 * Bundles features that don't justify their own sidebar entry, organised
 * into 4 group-tabs (each with its own sub-tab strip):
 *   Access · Governance · Integrations · Growth
 *
 *   Access:        Login attempts · API keys
 *   Governance:    DSR center · Documents repo
 *   Integrations:  Outbound webhooks · Knowledge base CMS
 *   Growth:        Cohort retention · Affiliate · Returns RMA
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Shield, ShieldAlert, Key, FileSearch, FolderOpen, Webhook, BookOpen,
  Users2, Gift, Undo2, Loader2, Plus, Trash2, Copy, Eye,
} from "lucide-react";
import {
  MgmtKpi, MgmtSectionHeader, MgmtEmpty, ReasonField, TABS_STYLE, TabBtn,
} from "../../components/AdminMgmtCommon";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const GROUPS = [
  { id: "access",       label: "Access",       icon: Shield, tabs: ["logins", "apikeys"] },
  { id: "governance",   label: "Governance",   icon: FileSearch, tabs: ["dsr", "docs"] },
  { id: "integrations", label: "Integrations", icon: Webhook, tabs: ["outwh", "kb"] },
  { id: "growth",       label: "Growth",       icon: Users2, tabs: ["cohorts", "affiliate", "returns"] },
];

const SUB_LABELS = {
  logins: { label: "Login attempts", icon: ShieldAlert },
  apikeys: { label: "API keys", icon: Key },
  dsr: { label: "GDPR / DSR", icon: FileSearch },
  docs: { label: "Documents", icon: FolderOpen },
  outwh: { label: "Outbound webhooks", icon: Webhook },
  kb: { label: "Knowledge base", icon: BookOpen },
  cohorts: { label: "Cohort retention", icon: Users2 },
  affiliate: { label: "Affiliate / Referrals", icon: Gift },
  returns: { label: "Returns / RMA", icon: Undo2 },
};

export default function AdminMgmtHub() {
  const [group, setGroup] = useState("access");
  const [sub, setSub] = useState("logins");

  const groupDef = GROUPS.find(g => g.id === group);
  useEffect(() => { setSub(groupDef.tabs[0]); /* eslint-disable-next-line */ }, [group]);

  return (
    <div className="p-6 space-y-5 max-w-7xl" data-testid="admin-mgmt-hub">
      <PageHeader title="Admin Hub" subtitle="Access · governance · integrations · growth — combined oversight surfaces."/>
      <div className={TABS_STYLE} data-testid="hub-groups">
        {GROUPS.map(g => <TabBtn key={g.id} active={group === g.id} onClick={() => setGroup(g.id)} icon={g.icon} label={g.label} testid={`hub-group-${g.id}`}/>)}
      </div>
      <div className="flex gap-1 flex-wrap" data-testid="hub-subs">
        {groupDef.tabs.map(t => {
          const meta = SUB_LABELS[t];
          const Icon = meta.icon;
          const active = sub === t;
          return (
            <button key={t} onClick={() => setSub(t)} data-testid={`hub-sub-${t}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border ${active ? "bg-foreground text-background border-foreground" : "bg-background border-border text-foreground/65 hover:border-foreground/40"}`}>
              <Icon className="w-3 h-3"/>{meta.label}
            </button>
          );
        })}
      </div>
      {sub === "logins"    && <LoginAttemptsTab/>}
      {sub === "apikeys"   && <ApiKeysTab/>}
      {sub === "dsr"       && <DsrTab/>}
      {sub === "docs"      && <DocsTab/>}
      {sub === "outwh"     && <OutboundWebhooksTab/>}
      {sub === "kb"        && <KbTab/>}
      {sub === "cohorts"   && <CohortsTab/>}
      {sub === "affiliate" && <AffiliateTab/>}
      {sub === "returns"   && <ReturnsTab/>}
    </div>
  );
}

/* Access · Login attempts */
function LoginAttemptsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("failed");
  const [days, setDays] = useState(7);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/access/login-attempts?result=${filter}&days=${days}&limit=200`); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, [filter, days]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Login attempts"
        subtitle={`${data.count} attempts · ${filter} only · last ${days}d`} onRefresh={load} busy={busy}
        action={
          <div className="flex gap-1">
            {[["", "All"], ["failed", "Failed"], ["success", "Success"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} className={`px-2 py-1 text-[11px] rounded border ${filter === v ? "bg-foreground text-background border-foreground" : "bg-background"}`} data-testid={`logins-filter-${v || "all"}`}>{l}</button>
            ))}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 border rounded-md px-2 text-[12px] bg-background ml-1">
              <option value={1}>24h</option><option value={7}>7d</option><option value={30}>30d</option>
            </select>
          </div>
        }/>
      {data.top_failed_ips?.length > 0 && (
        <div className="rounded-xl border p-3 bg-rose-50/40">
          <div className="text-[11.5px] font-semibold mb-2">Top failed IPs · ≥3 failures</div>
          <div className="flex flex-wrap gap-1.5" data-testid="top-failed-ips">
            {data.top_failed_ips.slice(0, 12).map(r => (
              <span key={r.ip} className="chip text-[10.5px] bg-background border-rose-200 text-rose-800">{r.ip} <span className="ml-1 mono opacity-65">×{r.fails}</span></span>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="logins-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Agent</th><th className="px-3 py-2">Result</th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map((r, i) => (
              <tr key={i} data-testid={`login-row-${i}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.email || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.ip || "—"}</td>
                <td className="px-3 py-2 text-[10.5px] text-foreground/55 max-w-xs truncate">{r.user_agent || "—"}</td>
                <td className="px-3 py-2"><span className={`chip text-[10px] ${r.success ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"}`}>{r.success ? "ok" : "fail"}</span></td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No login attempts logged in this window. Attempts are written to login_attempts collection on every /auth/login call."/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Access · API keys */
function ApiKeysTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/access/api-keys"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (kid) => {
    const reason = window.prompt("Revoke reason (min 3 chars)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.delete(`/admin/mgmt/access/api-keys/${kid}`, { data: { reason: reason.trim() } }); toast.success("Revoked"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="API keys" subtitle={`${data.count || 0} keys · machine credentials for backend integrations`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="apikey-new-btn"><Plus className="w-3 h-3 mr-1"/>Mint key</Button>}/>
      {newKey && (
        <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4" data-testid="apikey-new-banner">
          <div className="text-[13px] font-semibold text-emerald-900">New key minted — copy NOW, you won't see it again</div>
          <div className="mt-2 mono text-[12px] bg-background p-2 rounded-md flex items-center justify-between gap-2 break-all">
            <span data-testid="apikey-new-value">{newKey.key}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Copied"); }}><Copy className="w-3 h-3"/></Button>
          </div>
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setNewKey(null)}>Dismiss</Button>
        </div>
      )}
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="apikeys-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Label</th><th className="px-3 py-2">Prefix</th><th className="px-3 py-2">Scopes</th><th className="px-3 py-2">Created</th><th className="px-3 py-2">State</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(k => (
              <tr key={k.id} data-testid={`apikey-row-${k.id}`}>
                <td className="px-3 py-2 font-medium">{k.label}</td>
                <td className="px-3 py-2 mono text-[11px]">{k.key_prefix}…</td>
                <td className="px-3 py-2 text-[11px]">{(k.scopes || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{k.created_at ? new Date(k.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2"><span className={`chip text-[10px] ${k.revoked ? "bg-foreground/5" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>{k.revoked ? "revoked" : "live"}</span></td>
                <td className="px-3 py-2 text-right">{!k.revoked && <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.id}`}><Trash2 className="w-3 h-3"/></Button>}</td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No API keys minted yet."/></td></tr>}
          </tbody>
        </table>
      </div>
      <ApiKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(k) => { setNewKey(k); load(); }}/>
    </div>
  );
}

function ApiKeyDialog({ open, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLabel(""); setScopes(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (label.length < 2 || reason.trim().length < 3) { toast.error("Label + reason required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/mgmt/access/api-keys", { label, scopes: scopes.split(",").map(s => s.trim()).filter(Boolean), reason: reason.trim() });
      onCreated(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="apikey-dialog">
        <DialogHeader><DialogTitle>Mint API key</DialogTitle><DialogDescription>Generates a machine credential. The raw key is shown ONCE.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="apikey-label"/></div>
          <div><Label>Scopes (comma-separated)</Label><Input value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="e.g. read:invoices,read:orders" data-testid="apikey-scopes"/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="apikey-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Mint</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Governance · DSR */
function DsrTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/governance/dsr?status=${status}`); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (did, newStatus) => {
    const reason = window.prompt(`Mark this DSR as ${newStatus}? (reason, min 3 chars)`);
    if (!reason || reason.trim().length < 3) return;
    try { await api.patch(`/admin/mgmt/governance/dsr/${did}`, { status: newStatus, reason: reason.trim() }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="GDPR / DSR Request Center" subtitle={`${data.count || 0} requests · ${data.pending || 0} pending action`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="dsr-new-btn"><Plus className="w-3 h-3 mr-1"/>Log request</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="dsr-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Created</th><th className="px-3 py-2">User</th><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Deadline</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`dsr-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.user_email}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.kind}</span></td>
                <td className="px-3 py-2 mono text-[11px]">{r.deadline_at?.slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${r.status === "completed" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : r.status === "rejected" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  {(r.status === "open" || r.status === "in_progress") && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => resolve(r.id, "completed")} data-testid={`dsr-complete-${r.id}`}>Complete</Button>
                      <Button size="sm" variant="ghost" onClick={() => resolve(r.id, "rejected")} className="text-rose-700">Reject</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No data-subject requests logged yet. When a user emails support asking to export or delete their data, log it here so SLA is tracked."/></td></tr>}
          </tbody>
        </table>
      </div>
      <DsrDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function DsrDialog({ open, onClose, onSaved }) {
  const [email, setEmail] = useState(""); const [kind, setKind] = useState("export"); const [notes, setNotes] = useState(""); const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(""); setKind("export"); setNotes(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (!email || reason.trim().length < 3) { toast.error("All required"); return; }
    setBusy(true);
    try { await api.post("/admin/mgmt/governance/dsr", { user_email: email, kind, notes, reason: reason.trim() }); toast.success("Logged"); onSaved(); onClose(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dsr-dialog">
        <DialogHeader><DialogTitle>Log DSR request</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>User email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="dsr-email"/></div>
          <div>
            <Label>Kind</Label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="dsr-kind">
              <option value="export">Data export</option><option value="delete">Account deletion</option><option value="correction">Data correction</option><option value="portability">Portability</option><option value="access">Access request</option>
            </select>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="dsr-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Log</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Governance · Docs */
function DocsTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/governance/documents"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (did) => {
    const reason = window.prompt("Delete reason (min 3 chars)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.delete(`/admin/mgmt/governance/documents/${did}`, { data: { reason: reason.trim() } }); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Document repository" subtitle={`${data.count || 0} documents · contracts, SOC2 evidence, policies, vendor agreements`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="doc-new-btn"><Plus className="w-3 h-3 mr-1"/>Upload</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="docs-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Title</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Tags</th><th className="px-3 py-2">Uploaded</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`doc-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.title}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.category}</span></td>
                <td className="px-3 py-2 text-[10.5px] text-foreground/55">{(r.tags || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-foreground/60 hover:text-foreground inline-flex"><Eye className="w-3.5 h-3.5"/></a>}
                  <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No documents stored yet. Upload contracts, NDAs, SOC2 evidence, vendor agreements."/></td></tr>}
          </tbody>
        </table>
      </div>
      <DocDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function DocDialog({ open, onClose, onSaved }) {
  const [title, setTitle] = useState(""); const [category, setCategory] = useState("contract"); const [url, setUrl] = useState(""); const [tags, setTags] = useState(""); const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setCategory("contract"); setUrl(""); setTags(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (title.length < 2 || reason.trim().length < 3) { toast.error("Title + reason required"); return; }
    setBusy(true);
    try { await api.post("/admin/mgmt/governance/documents", { title, category, url, tags: tags.split(",").map(t => t.trim()).filter(Boolean), reason: reason.trim() }); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doc-dialog">
        <DialogHeader><DialogTitle>Add document</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title"/></div>
          <div>
            <Label>Category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="doc-category">
              <option value="contract">Contract</option><option value="nda">NDA</option><option value="soc2">SOC2 evidence</option><option value="policy">Policy</option><option value="vendor">Vendor agreement</option><option value="other">Other</option>
            </select>
          </div>
          <div><Label>URL (link to PDF / object storage)</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." data-testid="doc-url"/></div>
          <div><Label>Tags (comma-separated)</Label><Input value={tags} onChange={(e) => setTags(e.target.value)}/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="doc-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Integrations · Outbound webhooks */
function OutboundWebhooksTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSecret, setNewSecret] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/webhooks/outbound"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (sid) => {
    const reason = window.prompt("Disable reason (min 3 chars)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.delete(`/admin/mgmt/webhooks/outbound/${sid}`, { data: { reason: reason.trim() } }); toast.success("Disabled"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Outbound webhook subscriptions" subtitle={`${data.count || 0} subscriptions · platform fires events to these URLs`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="outwh-new-btn"><Plus className="w-3 h-3 mr-1"/>Subscribe</Button>}/>
      {newSecret && (
        <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-3" data-testid="outwh-new-secret-banner">
          <div className="text-[12.5px] font-semibold text-emerald-900">Signing secret — copy NOW</div>
          <div className="mt-1 mono text-[11.5px] bg-background p-2 rounded break-all">{newSecret}</div>
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setNewSecret(null)}>Dismiss</Button>
        </div>
      )}
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="outwh-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Label</th><th className="px-3 py-2">URL</th><th className="px-3 py-2">Events</th><th className="px-3 py-2">Delivered</th><th className="px-3 py-2">Failed</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`outwh-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.label || "—"}</td>
                <td className="px-3 py-2 mono text-[10.5px] max-w-xs truncate">{r.url}</td>
                <td className="px-3 py-2 text-[10.5px] text-foreground/55">{(r.events || []).join(", ")}</td>
                <td className="px-3 py-2 mono">{r.stats?.delivered || 0}</td>
                <td className={`px-3 py-2 mono ${r.stats?.failed > 0 ? "text-rose-700" : ""}`}>{r.stats?.failed || 0}</td>
                <td className="px-3 py-2 text-right">{r.enabled && <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-3 h-3"/></Button>}</td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No subscribers yet. Sellers/buyers can subscribe their own URLs to platform events (invoice.paid, order.created, etc.)."/></td></tr>}
          </tbody>
        </table>
      </div>
      <OutWhDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(s) => { setNewSecret(s); load(); }}/>
    </div>
  );
}

function OutWhDialog({ open, onClose, onCreated }) {
  const [url, setUrl] = useState(""); const [events, setEvents] = useState(""); const [label, setLabel] = useState(""); const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setUrl(""); setEvents(""); setLabel(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (!url || reason.trim().length < 3) { toast.error("URL + reason required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/mgmt/webhooks/outbound", { url, events: events.split(",").map(e => e.trim()).filter(Boolean), label, reason: reason.trim() });
      onCreated(r.data.signing_secret); onClose();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="outwh-dialog">
        <DialogHeader><DialogTitle>New outbound subscription</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="outwh-label"/></div>
          <div><Label>Callback URL</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." data-testid="outwh-url"/></div>
          <div><Label>Events (comma-separated)</Label><Input value={events} onChange={(e) => setEvents(e.target.value)} placeholder="invoice.paid,order.created" data-testid="outwh-events"/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="outwh-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Subscribe</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Integrations · KB */
function KbTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/kb/articles?q=${encodeURIComponent(q)}`); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const del = async (slug) => {
    const reason = window.prompt("Delete reason (min 3 chars)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.delete(`/admin/mgmt/kb/articles/${slug}`, { data: { reason: reason.trim() } }); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Knowledge base CMS" subtitle={`${data.count || 0} articles · serves the in-app Help widget`} onRefresh={load} busy={busy}
        action={<div className="flex gap-2"><Input placeholder="search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-44" data-testid="kb-search"/><Button size="sm" onClick={() => setEditing({ slug: "", title: "", body_md: "", category: "general", audience: "all", published: false })} data-testid="kb-new-btn"><Plus className="w-3 h-3 mr-1"/>New</Button></div>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="kb-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Slug</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Audience</th><th className="px-3 py-2">v</th><th className="px-3 py-2">State</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.slug} data-testid={`kb-row-${r.slug}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.slug}</td>
                <td className="px-3 py-2 font-medium max-w-md truncate">{r.title}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.audience}</span></td>
                <td className="px-3 py-2 mono">{r.version}</td>
                <td className="px-3 py-2"><span className={`chip text-[10px] ${r.published ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-foreground/5"}`}>{r.published ? "live" : "draft"}</span></td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r.slug)}><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No KB articles yet. Author the help content that powers the Help widget in-app."/></td></tr>}
          </tbody>
        </table>
      </div>
      <KbDialog editing={editing} onClose={() => setEditing(null)} onSaved={load}/>
    </div>
  );
}

function KbDialog({ editing, onClose, onSaved }) {
  const [slug, setSlug] = useState(""); const [title, setTitle] = useState(""); const [body, setBody] = useState(""); const [audience, setAudience] = useState("all"); const [category, setCategory] = useState("general"); const [published, setPublished] = useState(false); const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  const open = !!editing;
  useEffect(() => {
    if (!editing) return;
    setSlug(editing.slug || ""); setTitle(editing.title || ""); setBody(editing.body_md || "");
    setAudience(editing.audience || "all"); setCategory(editing.category || "general"); setPublished(!!editing.published); setReason("");
  }, [editing]);
  const submit = async () => {
    if (!slug || title.length < 2 || reason.trim().length < 3) { toast.error("Slug + title + reason required"); return; }
    setBusy(true);
    try { await api.put(`/admin/mgmt/kb/articles/${slug}`, { title, body_md: body, category, audience, tags: [], published, reason: reason.trim() }); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="kb-dialog">
        <DialogHeader><DialogTitle>{editing?.slug ? `Edit · ${editing.slug}` : "New article"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value.replace(/\s+/g, "-").toLowerCase())} disabled={!!editing?.slug} data-testid="kb-slug"/></div>
            <div>
              <Label>Audience</Label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="kb-audience">
                <option value="all">All</option><option value="buyer">Buyer</option><option value="seller">Seller</option><option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="kb-title"/></div>
          <div><Label>Body (markdown)</Label><Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="mono text-[12px]" data-testid="kb-body"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)}/></div>
            <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="kb-published"/><span className="text-[12.5px]">Publish</span></label>
          </div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="kb-save">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Growth · Cohorts */
function CohortsTab() {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("buyer");
  const [months, setMonths] = useState(6);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/analytics/cohorts?months=${months}&role=${role}`); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, [role, months]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Cohort retention" subtitle={`Signup month · % active in subsequent months · role: ${role}`} onRefresh={load} busy={busy}
        action={
          <div className="flex gap-1">
            {["buyer", "seller"].map(r => (
              <button key={r} onClick={() => setRole(r)} className={`px-2 py-1 text-[11px] rounded border capitalize ${role === r ? "bg-foreground text-background border-foreground" : "bg-background"}`} data-testid={`cohort-role-${r}`}>{r}</button>
            ))}
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-8 border rounded-md px-2 text-[12px] bg-background ml-1">
              <option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option>
            </select>
          </div>
        }/>
      <div className="rounded-xl border overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="cohorts-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Cohort</th><th className="px-3 py-2">Size</th><th className="px-3 py-2">M0</th><th className="px-3 py-2">M1</th><th className="px-3 py-2">M2</th><th className="px-3 py-2">M3</th><th className="px-3 py-2">M6</th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.cohorts || []).map(c => (
              <tr key={c.cohort} data-testid={`cohort-${c.cohort}`}>
                <td className="px-3 py-2 mono">{c.cohort}</td>
                <td className="px-3 py-2 mono">{c.size}</td>
                {(c.retained || []).map((r, i) => (
                  <td key={i} className="px-3 py-2">
                    {r === null ? <span className="text-foreground/30">—</span> : (
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1.5 bg-foreground/5 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${r.pct}%` }}/>
                        </div>
                        <span className="mono text-[11px]">{r.pct}%</span>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {(data.cohorts || []).length === 0 && <tr><td colSpan={7}><MgmtEmpty/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Growth · Affiliate */
function AffiliateTab() {
  const [data, setData] = useState({ codes: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/affiliate"); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Affiliate / Referral codes" subtitle={`${data.count || 0} codes · ${money(data.total_paid_usd || 0)} paid out across ${data.payout_count || 0} payouts`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="aff-new-btn"><Plus className="w-3 h-3 mr-1"/>New code</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="aff-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Owner</th><th className="px-3 py-2">Payout %</th><th className="px-3 py-2">Signups</th><th className="px-3 py-2">GMV</th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.codes || []).map(c => (
              <tr key={c.id} data-testid={`aff-row-${c.code}`}>
                <td className="px-3 py-2 mono font-semibold">{c.code}</td>
                <td className="px-3 py-2 text-[11px]">{c.owner_user_id ? c.owner_user_id.slice(0, 14) + "…" : <span className="text-foreground/45">—</span>}</td>
                <td className="px-3 py-2 mono">{c.payout_pct}%</td>
                <td className="px-3 py-2 mono">{c.stats?.signups || 0}</td>
                <td className="px-3 py-2 mono">{money(c.stats?.total_gmv || 0)}</td>
              </tr>
            ))}
            {(data.codes || []).length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No referral codes yet."/></td></tr>}
          </tbody>
        </table>
      </div>
      <AffiliateDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function AffiliateDialog({ open, onClose, onSaved }) {
  const [code, setCode] = useState(""); const [pct, setPct] = useState(10); const [label, setLabel] = useState(""); const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCode(""); setPct(10); setLabel(""); setReason(""); } }, [open]);
  const submit = async () => {
    if (code.length < 3 || reason.trim().length < 3) { toast.error("Code + reason required"); return; }
    setBusy(true);
    try { await api.post("/admin/mgmt/affiliate", { code, payout_pct: Number(pct), label, reason: reason.trim() }); toast.success("Created"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="aff-dialog">
        <DialogHeader><DialogTitle>New referral code</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Code (auto-uppercased)</Label><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, ""))} data-testid="aff-code"/></div>
          <div><Label>Payout %</Label><Input type="number" value={pct} min={0} max={100} onChange={(e) => setPct(e.target.value)} data-testid="aff-pct"/></div>
          <div><Label>Label / notes</Label><Input value={label} onChange={(e) => setLabel(e.target.value)}/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="aff-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Growth · Returns */
function ReturnsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/returns?status=${status}`); setData(r.data); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, newStatus) => {
    const reason = window.prompt(`Mark return ${newStatus}? (reason, min 3 chars)`);
    if (!reason || reason.trim().length < 3) return;
    try { await api.patch(`/admin/mgmt/returns/${rid}`, { status: newStatus, reason: reason.trim() }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Returns / RMA" subtitle={`${data.count || 0} returns · ${money(data.total_value)} total value`} onRefresh={load} busy={busy}
        action={
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 border rounded-md px-2 text-[12px] bg-background" data-testid="returns-filter">
            <option value="">All</option><option value="open">Open</option><option value="approved">Approved</option><option value="in_transit">In transit</option><option value="received">Received</option><option value="refunded">Refunded</option>
          </select>
        }/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {Object.entries(data.by_status || {}).map(([k, v]) => <MgmtKpi key={k} label={k} value={v}/>)}
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="returns-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Ref</th><th className="px-3 py-2">Order</th><th className="px-3 py-2">Value</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`return-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.ref || r.id.slice(0, 12)}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.order_code || "—"}</td>
                <td className="px-3 py-2 mono">{money(r.value || 0)}</td>
                <td className="px-3 py-2 max-w-xs truncate">{r.reason || "—"}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.status}</span></td>
                <td className="px-3 py-2 text-right space-x-1">
                  {r.status === "open" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => decide(r.id, "approved")} data-testid={`return-approve-${r.id}`}>Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => decide(r.id, "denied")} className="text-rose-700">Deny</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No return requests yet. Returns logged in the returns collection appear here for admin decision."/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
